import { getRegionByCode } from '@luxuryescapes/lib-regions';

import { CommissionRule } from '~/types/services/agentHub';

export type CommissionsConditionTypes =
  | 'location'
  | 'departure'
  | 'arrival'
  | 'vendor'
  | 'agency'
  | 'agent'
  | 'productType'
  | 'bundledWithOffer'
  | 'offer'
  | 'margin'
  | 'tier'
  | 'dateRange'
  | 'flightClass'
  | 'bundledWithTypes'
  | 'bundleTotalPrice'
  | 'flightCarrier'
  | 'minTotalSpend'
  | 'tags';

export type CommissionOption = Array<{
  value: string | number;
  label: string;
}>;

export type RuleConditionType = [keyof RuleConditions, CommissionOption];

export type RuleConditions = {
  [key in CommissionsConditionTypes]: CommissionOption;
};

export interface RuleCondition {
  type: keyof RuleConditions;
  value: CommissionOption;
}

export interface CommissionFormData {
  description: string;
  startDate: string;
  endDate: string;
  regions: Array<string>;
  discountType: string;
  discountAmount: number;
  /** Base commissions only; nullable */
  priority?: number | null;
  /** Base commissions only */
  canHaveAdditionalCommissions?: boolean;
}

export const INITIAL_COMMISSION_CONDITIONS: RuleConditions = {
  agency: [],
  agent: [],
  offer: [],
  vendor: [],
  location: [],
  departure: [],
  arrival: [],
  bundledWithOffer: [],
  flightClass: [],
  productType: [],
  flightCarrier: [],
  margin: [],
  bundleTotalPrice: [],
  minTotalSpend: [],
  tier: [],
  dateRange: [],
  bundledWithTypes: [],
  tags: [],
};

export const ruleConditionsToTextMap = new Map<keyof RuleConditions, string>([
  ['agency', 'Agencies'],
  ['agent', 'Agents'],
  ['offer', 'Offers'],
  ['vendor', 'Vendors'],
  ['location', 'Locations'],
  ['departure', 'Departure location'],
  ['arrival', 'Arrival location'],
  ['productType', 'Product type'],
  ['flightCarrier', 'Flight carriers'],
  ['bundledWithOffer', 'Bundled with offer'],
  ['bundledWithTypes', 'Bundled with types'],
  ['bundleTotalPrice', 'Bundle total price'],
  ['minTotalSpend', 'Min total spend'],
  ['flightClass', 'Flight class'],
  ['margin', 'Margin'],
  ['tier', 'Tier'],
  ['dateRange', 'Date range'],
  ['tags', 'User tags'],
]);

export const COMMISSION_RULE_CONDITION_TYPES: Array<CommissionsConditionTypes> = [
  'location',
  'departure',
  'arrival',
  'vendor',
  'agency',
  'agent',
  'tags',
  'productType',
  'flightCarrier',
  'bundledWithOffer',
  'flightClass',
  'bundledWithTypes',
  'bundleTotalPrice',
  'minTotalSpend',
  'offer',
  'margin',
  'tier',
  'dateRange',
];

export const REGION_SORT_ORDER = [
  'Australia',
  'New Zealand',
  'United States',
  'United Kingdom',
  'Singapore',
  'India',
  'Germany',
  'Italy',
  'Canada',
  'China',
  'France',
  'Hong Kong',
  'Indonesia',
  'Ireland',
  'Israel',
  'Japan',
  'Korea',
  'Macau',
  'Malaysia',
  'Netherlands',
  'Philippines',
  'Qatar',
  'Russia',
  'Saudi Arabia',
  'South Africa',
  'Spain',
  'Taiwan',
  'Thailand',
  'United Arab Emirates',
  'Vietnam',
];

export const USER_TAG_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'no_booking_last_6_months', label: 'No booking in last 6 months' },
  { value: 'no_booking_last_12_months', label: 'No booking in last 12 months' },
];

export const PRODUCT_TYPES_OPTIONS = [
  'addon',
  'always_on',
  'bedbank_hotel',
  'bundle_and_save',
  'car_hire',
  'connection_tour',
  'corporate',
  'cruise',
  'cruise-exclusive',
  'cruise-flash',
  'cruise-tactical',
  'direct_tour',
  'experience',
  'flight',
  'flash',
  'hotel',
  'last_minute_hotel',
  'limited_time_special',
  'partner_tour',
  'rental',
  'tactical_ao_hotel',
  'tour',
  'transfer',
  'ultra_lux_tour',
  'ultralux_hotel',
  'insider_series_tour',
];

export const TIER_OPTIONS: CommissionRule[0]['tier'] = ['tier1', 'tier2', 'tier3'];

export const AVAILABLE_REGIONS = [
  { value: 'AU', label: 'Australia' },
  { value: 'NZ', label: 'New Zealand' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'US', label: 'United States' },
];

export const AVAILABLE_REGIONS_LIB_REGIONS = AVAILABLE_REGIONS.map((region) => getRegionByCode(region.value));

export const AGENT_HUB_BRANDS = {
  leagenthub: 'LE Agent Hub',
  leagenthub_cvfr: 'CVFR Holidays',
} as const;

export type AgentHubBrand = keyof typeof AGENT_HUB_BRANDS;

export function ensureAgentHubBrands(brand: string): AgentHubBrand {
  if (Object.keys(AGENT_HUB_BRANDS).includes(brand)) {
    return brand as AgentHubBrand;
  }
  throw new Error(`Invalid agent hub brand: ${brand}`);
}

export function labelForUserTagValue(value: string) {
  return USER_TAG_OPTIONS.find((o) => o.value === value)?.label ?? value;
}
